import React, { useRef, useState, useEffect } from "react";
import EmojiPicker from "emoji-picker-react";
import { Paperclip, Smile, Tag, Mic, FileText } from "lucide-react";
import { TAG_META } from "./tagMeta";

const NEON = "#00FF99";

const GIPHY_KEY      = process.env.REACT_APP_GIPHY_API_KEY || "";
const GIPHY_SEARCH   = "https://api.giphy.com/v1/gifs/search";
const GIPHY_TRENDING = "https://api.giphy.com/v1/gifs/trending";

export const TAGS = [
  { value:"none",      label:"No Tag",    emoji:"",   color:"rgba(255,255,255,0.3)",  desc:"" },
  { value:"urgent",    label:"Urgent",    emoji:"🚨", color:"#ef4444",                desc:"Needs immediate attention" },
  { value:"important", label:"Important", emoji:"⭐", color:"#facc15",                desc:"High priority, not time-critical" },
  { value:"todo",      label:"To Do",     emoji:"✅", color:"#60a5fa",                desc:"Action required from recipient" },
  { value:"checkin",   label:"Check In",  emoji:"📋", color:"#a78bfa",                desc:"Requesting a status update" },
  { value:"fyi",       label:"FYI",       emoji:"📌", color:"#94a3b8",                desc:"Awareness only, no action needed" },
];

/* ── GIF Picker ─────────────────────────────────────────────── */
function GifPicker({ onSelect, onClose }) {
  const [query, setQuery]   = useState("");
  const [gifs, setGifs]     = useState([]);
  const [loading, setLoading] = useState(true);
  const timer = useRef(null);

  const load = async (q) => {
    setLoading(true);
    try {
      const url = q
        ? `${GIPHY_SEARCH}?api_key=${GIPHY_KEY}&q=${encodeURIComponent(q)}&limit=24&rating=g`
        : `${GIPHY_TRENDING}?api_key=${GIPHY_KEY}&limit=24&rating=g`;
      const d = await (await fetch(url)).json();
      setGifs(d.data || []);
    } catch (_) { setGifs([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(""); }, []);

  if (!GIPHY_KEY) return (
    <div style={{ width:280, background:"#111", border:`1px solid ${NEON}30`, borderRadius:14, padding:18, textAlign:"center" }}>
      <p style={{ color:"rgba(255,255,255,0.4)", fontSize:12, lineHeight:1.7 }}>
        Add <code style={{ color:NEON }}>REACT_APP_GIPHY_API_KEY</code> to <code style={{ color:NEON }}>.env</code> to enable GIFs.
      </p>
      <a href="https://developers.giphy.com/dashboard/" target="_blank" rel="noreferrer"
        style={{ color:NEON, fontSize:12, display:"block", marginTop:6 }}>Get a free key →</a>
      <button onClick={onClose} style={{ marginTop:10, padding:"4px 14px", borderRadius:8, background:"rgba(255,255,255,0.06)", color:"rgba(255,255,255,0.4)", border:"1px solid rgba(255,255,255,0.1)", cursor:"pointer", fontSize:12 }}>Close</button>
    </div>
  );

  return (
    <div style={{ width:280, maxHeight:340, display:"flex", flexDirection:"column", background:"#111", border:"1px solid rgba(255,255,255,0.1)", borderRadius:14, overflow:"hidden", boxShadow:"0 8px 32px rgba(0,0,0,0.6)" }}>
      <div style={{ padding:"8px 8px 4px", flexShrink:0 }}>
        <input value={query} onChange={e => { setQuery(e.target.value); clearTimeout(timer.current); timer.current=setTimeout(()=>load(e.target.value),400); }}
          placeholder="Search GIFs…"
          style={{ width:"100%", background:"rgba(255,255,255,0.07)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:9, padding:"6px 10px", color:"#fff", fontSize:12, outline:"none", boxSizing:"border-box" }} />
      </div>
      <div style={{ flex:1, overflowY:"auto", padding:"4px 6px 6px", display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:3 }}>
        {loading
          ? <div style={{ gridColumn:"1/-1", display:"flex", justifyContent:"center", padding:20 }}><div style={{ width:16, height:16, borderRadius:"50%", border:`2px solid ${NEON}`, borderTopColor:"transparent", animation:"gs 0.7s linear infinite" }}/></div>
          : gifs.length === 0
            ? <p style={{ gridColumn:"1/-1", color:"rgba(255,255,255,0.3)", fontSize:12, textAlign:"center", padding:"14px 0" }}>No GIFs found</p>
            : gifs.map(g => (
              <button key={g.id} onClick={() => onSelect(g.images.fixed_height_small?.url || g.images.original?.url)}
                style={{ border:"none", borderRadius:6, overflow:"hidden", cursor:"pointer", padding:0, background:"#000", aspectRatio:"1" }}>
                <img src={g.images.fixed_height_small?.url} alt={g.title} style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }} />
              </button>
            ))
        }
      </div>
      <p style={{ padding:"2px 8px 5px", textAlign:"right", color:"rgba(255,255,255,0.15)", fontSize:9, flexShrink:0 }}>Powered by GIPHY</p>
      <style>{`@keyframes gs{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

/* ── Tag selector ───────────────────────────────────────────── */
function TagSelector({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const active = TAGS.find(t => t.value === value) || TAGS[0];
  const meta   = TAG_META[value] || null;                       // shared rail mapping (null for "none")
  const clr    = meta ? `rgba(${meta.rgb},0.95)` : active.color; // tag color per TAG_META

  useEffect(() => {
    if (!open) return;
    const h = e => { if (!e.target.closest("[data-tag-sel]")) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  return (
    <div style={{ position:"relative" }} data-tag-sel>
      <button onClick={() => setOpen(v => !v)} title="Tag this message"
        style={{
          width:30, height:30, borderRadius:"50%", cursor:"pointer", border:"none",
          background: value === "none" ? "rgba(255,255,255,0.05)" : meta ? `rgba(${meta.rgb},0.14)` : `${active.color}18`,
          outline: value !== "none" ? (meta ? `1px solid rgba(${meta.rgb},0.40)` : `1px solid ${active.color}45`) : "1px solid rgba(255,255,255,0.1)",
          display:"flex", alignItems:"center", justifyContent:"center",
          fontSize:13, color: value === "none" ? "rgba(255,255,255,0.4)" : clr,
          transition:"all 0.15s",
        }}>
        {value === "none"
          ? <Tag size={16} strokeWidth={1.9} />
          : meta
            ? <meta.Icon size={16} strokeWidth={1.9} />
            : active.emoji}
      </button>

      {open && (
        <div style={{
          position:"absolute", bottom:38, left:0, zIndex:80,
          background:"#111", border:"1px solid rgba(255,255,255,0.1)",
          borderRadius:14, padding:6, minWidth:190,
          boxShadow:"0 8px 32px rgba(0,0,0,0.7)",
        }}>
          {TAGS.map(t => {
            const m = TAG_META[t.value]; // shared rail mapping (undefined for "none")
            return (
            <button key={t.value}
              onClick={() => { onChange(t.value); setOpen(false); }}
              style={{
                width:"100%", display:"flex", alignItems:"center", gap:8,
                padding:"7px 10px", borderRadius:10, border:"none", cursor:"pointer",
                background: value === t.value ? "rgba(255,255,255,0.08)" : "transparent",
                transition:"background 0.1s",
              }}
              onMouseEnter={e => e.currentTarget.style.background="rgba(255,255,255,0.06)"}
              onMouseLeave={e => e.currentTarget.style.background=value===t.value?"rgba(255,255,255,0.08)":"transparent"}>
              <span style={{ width:18, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                {m ? <m.Icon size={15} strokeWidth={1.9} color={`rgba(${m.rgb},0.95)`} /> : <span style={{ fontSize:14 }}>{t.emoji||"—"}</span>}
              </span>
              <div style={{ flex:1, textAlign:"left" }}>
                <p style={{ fontSize:12, fontWeight:500, margin:0, color:t.value==="none"?"rgba(255,255,255,0.45)":m?`rgba(${m.rgb},0.95)`:t.color }}>{t.label}</p>
                {t.desc && <p style={{ fontSize:10, color:"rgba(255,255,255,0.2)", margin:0 }}>{t.desc}</p>}
              </div>
            </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   MAIN
   Supports both new props (value/onChange/onSend) and legacy
   KweryAI props (userInput/setUserInput/handleSend).
════════════════════════════════════════════════════════════════ */
const MessageInputBox = ({
  // new unified props
  value, onChange, onSend, placeholder = "Type a message…", sending = false,
  onFileChosen, stagedFile, onRemoveFile, uploadPct = null,
  // legacy KweryAI props
  userInput, setUserInput, handleSend, validateAndAttachFiles,
  messageTag: externalTag, setMessageTag: setExternalTag,
}) => {
  const text    = value    ?? userInput    ?? "";
  const setText = onChange ?? setUserInput ?? (() => {});
  const doSend  = onSend   ?? handleSend   ?? (() => {});
  const doFile  = onFileChosen ?? validateAndAttachFiles ?? null;

  const fileRef   = useRef(null);
  const textaRef  = useRef(null);
  const emojiPickerRef = useRef(null); // wrapper around <EmojiPicker>
  const emojiBtnRef    = useRef(null); // the 😊 toggle button
  const pendingCaret   = useRef(null); // caret pos to restore after an emoji insert

  const [showEmoji, setShowEmoji] = useState(false);
  const [showGif,   setShowGif]   = useState(false);
  const [internalTag, setInternalTag] = useState("none");

  // ── Web Speech API (KIRA voice input) ──
  // Hidden entirely when the browser doesn't support it.
  const recognitionRef = useRef(null);
  const [voiceSupported, setVoiceSupported] = useState(false);
  const [listening,      setListening]      = useState(false);
  const [voiceTooltip,   setVoiceTooltip]   = useState(null);

  useEffect(() => {
    const SR = typeof window !== "undefined" &&
      (window.SpeechRecognition || window.webkitSpeechRecognition);
    if (!SR) return;
    const rec = new SR();
    rec.lang = "en-GB";
    rec.continuous = false;
    rec.interimResults = true;

    rec.onresult = (e) => {
      let interim = "";
      let final   = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (r.isFinal) final += r[0].transcript;
        else interim += r[0].transcript;
      }
      if (final) {
        setText(prev => (prev ? prev + " " : "") + final.trim());
      } else if (interim) {
        // Show interim live in the textarea
        setText(prev => prev);
      }
    };
    rec.onerror = (ev) => {
      setListening(false);
      if (ev.error === "not-allowed" || ev.error === "service-not-allowed") {
        setVoiceTooltip("Microphone access needed for voice input");
        setTimeout(() => setVoiceTooltip(null), 3500);
      }
    };
    rec.onend = () => setListening(false);

    recognitionRef.current = rec;
    setVoiceSupported(true);
    return () => { try { rec.abort(); } catch {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleVoice = () => {
    const rec = recognitionRef.current;
    if (!rec) return;
    if (listening) {
      try { rec.stop(); } catch {}
      setListening(false);
      return;
    }
    try {
      rec.start();
      setListening(true);
    } catch (err) {
      // start() can throw if already started or if no permission yet
      setVoiceTooltip("Could not start voice input");
      setTimeout(() => setVoiceTooltip(null), 2500);
    }
  };

  // Support externally controlled tag (for KweryAI chat) or internal state
  const tag    = externalTag    ?? internalTag;
  const setTag = setExternalTag ?? setInternalTag;

  const activeTag  = TAGS.find(t => t.value === tag) || TAGS[0];
  const activeMeta = TAG_META[tag] || null;   // shared rail mapping (null for "none")
  const canSend    = (text.trim() || stagedFile) && !sending;

  // Emoji picker close-behaviour: stays open across picks. Closes ONLY on
  // (a) mousedown outside both the picker and its toggle button, or (b) Escape.
  // Toggling the button again is handled by its own onClick; the mousedown guard
  // excludes the button so it never open-then-instantly-closes (no double-fire).
  // (The GIF picker was removed earlier; its state persists as dead code and no
  //  longer needs an outside-click handler.)
  useEffect(() => {
    if (!showEmoji) return;
    const onDown = e => {
      if (emojiPickerRef.current?.contains(e.target)) return; // click inside picker
      if (emojiBtnRef.current?.contains(e.target))    return; // click on toggle button
      setShowEmoji(false);
    };
    const onKey = e => { if (e.key === "Escape") setShowEmoji(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [showEmoji]);

  // Restore the caret after an emoji is inserted (runs once the controlled
  // textarea has committed the new value), keeping focus in the input.
  useEffect(() => {
    if (pendingCaret.current == null) return;
    const pos = pendingCaret.current;
    pendingCaret.current = null;
    const el = textaRef.current;
    if (!el) return;
    el.focus();
    try { el.setSelectionRange(pos, pos); } catch {}
  }, [text]);

  const handleEmoji = (d) => {
    const emoji = d.emoji;
    const el    = textaRef.current;
    // Insert at the current caret position; fall back to append if no selection.
    if (el && typeof el.selectionStart === "number") {
      const start = el.selectionStart;
      const end   = el.selectionEnd;
      setText(text.slice(0, start) + emoji + text.slice(end));
      pendingCaret.current = start + emoji.length; // restored in the [text] effect
    } else {
      setText((text || "") + emoji);
      pendingCaret.current = (text || "").length + emoji.length;
    }
    // Keep the picker OPEN so multiple emoji can be picked in a row.
  };

  const handleGif = (url) => {
    setText(prev => (prev ? prev + " " : "") + `[gif:${url}]`);
    setShowGif(false);
    textaRef.current?.focus();
  };

  const send = (e) => {
    if (!canSend) return;
    doSend(e, tag);
    setInternalTag("none");
    setShowEmoji(false); // sending (Enter or Send) closes the emoji picker
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault(); // stop newline
      send(e);
    }
    // Shift+Enter falls through naturally → textarea inserts newline
  };

  const fmtSize = b => b < 1024 ? `${b} B` : b < 1048576 ? `${(b/1024).toFixed(1)} KB` : `${(b/1048576).toFixed(1)} MB`;

  const IconBtn = React.forwardRef(({ onClick, title, active: a, children }, ref) => (
    <button ref={ref} onClick={onClick} title={title} data-picker-root
      style={{
        width:30, height:30, borderRadius:"50%", cursor:"pointer", border:"none", fontSize:13,
        display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
        background: a ? `${NEON}18` : "rgba(255,255,255,0.05)",
        color:      a ? NEON         : "rgba(255,255,255,0.45)",
        outline:    a ? `1px solid ${NEON}40` : "1px solid rgba(255,255,255,0.1)",
        transition:"all 0.15s",
      }}>
      {children}
    </button>
  ));

  return (
    <div style={{ position:"relative", width:"100%" }} data-picker-root>

      {/* ── Pickers ── */}
      {showEmoji && (
        <div ref={emojiPickerRef} style={{ position:"absolute", bottom:"calc(100% + 8px)", right:0, zIndex:70 }} data-picker-root>
          <EmojiPicker onEmojiClick={handleEmoji} theme="dark" width={300} height={380} />
        </div>
      )}
      {showGif && (
        <div style={{ position:"absolute", bottom:"calc(100% + 8px)", right:0, zIndex:70 }} data-picker-root>
          <GifPicker onSelect={handleGif} onClose={() => setShowGif(false)} />
        </div>
      )}

      {/* ── Active tag badge ── */}
      {tag !== "none" && (
        <div style={{
          display:"inline-flex", alignItems:"center", gap:5,
          marginBottom:6, padding:"3px 8px 3px 6px", borderRadius:8,
          background: activeMeta ? `rgba(${activeMeta.rgb},0.12)` : `${activeTag.color}12`,
          border: activeMeta ? `1px solid rgba(${activeMeta.rgb},0.30)` : `1px solid ${activeTag.color}30`,
        }}>
          {activeMeta
            ? <activeMeta.Icon size={12} strokeWidth={1.9} color={`rgba(${activeMeta.rgb},0.95)`} />
            : <span style={{ fontSize:12 }}>{activeTag.emoji}</span>}
          <span style={{ fontSize:11, color: activeMeta ? `rgba(${activeMeta.rgb},0.95)` : activeTag.color, fontWeight:600 }}>{activeTag.label}</span>
          <button onClick={() => setTag("none")}
            style={{ color: activeMeta ? `rgba(${activeMeta.rgb},0.6)` : `${activeTag.color}60`, background:"none", border:"none", cursor:"pointer", fontSize:11, padding:"0 2px", lineHeight:1 }}>
            ✕
          </button>
        </div>
      )}

      {/* ── Staged file chip ── */}
      {stagedFile && (
        <div style={{ marginBottom:6, display:"inline-flex", alignItems:"center", gap:8,
          padding:"5px 10px", borderRadius:12, background:"rgba(0,255,153,0.06)", border:`1px solid ${NEON}25`, maxWidth:220 }}>
          <FileText size={18} strokeWidth={1.8} style={{ flexShrink:0, color:"rgba(255,255,255,0.7)" }} />
          <div style={{ flex:1, minWidth:0 }}>
            <p style={{ color:"#fff", fontSize:11, fontWeight:500, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", margin:0 }}>{stagedFile.name}</p>
            <p style={{ color:"rgba(255,255,255,0.3)", fontSize:10, margin:0 }}>{fmtSize(stagedFile.size)}</p>
          </div>
          <button onClick={onRemoveFile} style={{ color:"rgba(255,255,255,0.3)", background:"none", border:"none", cursor:"pointer", fontSize:13, flexShrink:0 }}>✕</button>
        </div>
      )}

      {/* ── Upload progress ── */}
      {uploadPct !== null && (
        <div style={{ marginBottom:4 }}>
          <div style={{ height:3, background:"rgba(255,255,255,0.06)", borderRadius:2, overflow:"hidden" }}>
            <div style={{ height:"100%", width:`${uploadPct}%`, background:NEON, transition:"width 0.1s" }} />
          </div>
          <p style={{ color:"rgba(255,255,255,0.3)", fontSize:10, marginTop:2 }}>Uploading… {uploadPct}%</p>
        </div>
      )}

      {/* ── Input bar ── */}
      <div style={{
        display:"flex", alignItems:"flex-end", gap:6,
        background:"#0d0d0d",
        border:`1px solid ${tag !== "none" ? activeTag.color+"40" : "rgba(255,255,255,0.10)"}`,
        borderRadius:22, padding:"8px 8px 8px 14px",
        transition:"border-color 0.2s",
      }}>
        {/* File attach */}
        <IconBtn onClick={() => fileRef.current?.click()} title="Attach file"><Paperclip size={16} strokeWidth={1.9} /></IconBtn>
        <input ref={fileRef} type="file" style={{ display:"none" }}
          onChange={e => {
            const f = e.target.files[0];
            if (f && doFile) doFile(f);
            e.target.value = "";
          }} />

        {/* ── TEXTAREA — supports Shift+Enter newline ── */}
        <textarea
          ref={textaRef}
          value={text}
          rows={1}
          onChange={e => {
            setText(e.target.value);
            // auto-grow
            e.target.style.height = "auto";
            e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
          }}
          onKeyDown={onKey}
          placeholder={stagedFile ? "Add a caption…" : placeholder}
          style={{
            flex:1, background:"transparent", color:"#fff", fontSize:14,
            border:"none", outline:"none", resize:"none", lineHeight:1.5,
            minHeight:22, maxHeight:120, overflowY:"auto",
            fontFamily:"inherit", padding:"1px 0",
          }}
        />

        {/* GIF button removed in composer polish. The GifPicker component, the
            handleGif handler, and the showGif render block are intentionally
            kept below as dead (unreachable) code so the feature can be restored.
            TODO: re-add a GIF trigger button here to re-enable the GIF picker. */}

        {/* Emoji */}
        <IconBtn ref={emojiBtnRef} onClick={() => { setShowEmoji(v=>!v); setShowGif(false); }} title="Emoji" active={showEmoji}>
          <Smile size={16} strokeWidth={1.9} />
        </IconBtn>

        {/* Tag */}
        <TagSelector value={tag} onChange={setTag} />

        {/* Mic — Web Speech API. Hidden if unsupported. */}
        {voiceSupported && (
          <div style={{ position: "relative" }}>
            <button
              type="button"
              onClick={toggleVoice}
              title={listening ? "Stop listening" : "Voice input"}
              aria-pressed={listening}
              style={{
                width: 30, height: 30, borderRadius: "50%", cursor: "pointer",
                border: "none", fontSize: 13, flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                background: listening ? `${NEON}25` : "rgba(255,255,255,0.05)",
                color:      listening ? NEON          : "rgba(255,255,255,0.45)",
                outline:    listening ? `1px solid ${NEON}` : "1px solid rgba(255,255,255,0.1)",
                animation:  listening ? "mic-pulse 1.2s ease-in-out infinite" : "none",
                transition: "all 0.15s",
              }}
            >
              <Mic size={16} strokeWidth={1.9} />
            </button>
            {voiceTooltip && (
              <div
                role="tooltip"
                style={{
                  position: "absolute", bottom: 38, right: 0, zIndex: 80,
                  whiteSpace: "nowrap",
                  background: "#111", color: "#fff", fontSize: 11,
                  padding: "5px 10px", borderRadius: 8,
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                {voiceTooltip}
              </div>
            )}
            <style>{`
              @keyframes mic-pulse {
                0%, 100% { box-shadow: 0 0 0 0 rgba(0,255,153,0.4); }
                50%      { box-shadow: 0 0 0 8px rgba(0,255,153,0); }
              }
            `}</style>
          </div>
        )}

        {/* Send */}
        <button onClick={send} disabled={!canSend}
          style={{
            padding:"8px 18px", borderRadius:999, flexShrink:0,
            background: canSend ? NEON : "rgba(0,255,153,0.12)",
            border:"none", cursor: canSend ? "pointer" : "not-allowed",
            color: canSend ? "#000" : "rgba(0,255,153,0.3)",
            fontSize:12.5, fontWeight:600, transition:"all 0.15s",
          }}
          onMouseEnter={e => { if(canSend) e.currentTarget.style.boxShadow="0 0 14px rgba(0,255,153,0.4)"; }}
          onMouseLeave={e => e.currentTarget.style.boxShadow="none"}>
          {sending ? "…" : "Send"}
        </button>
      </div>
    </div>
  );
};

export default MessageInputBox;
